import React, { useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { CheckCircle2, Play, Circle, Clock, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";

const OUTCOMES = ["Risposta positiva", "Nessuna risposta", "Richiamare", "Non interessato", "Appuntamento fissato"];
const CHANNELS = ["Telefonata", "WhatsApp", "Email", "Instagram DM", "Di persona"];
const EMPTY_LOG = { channel: "Telefonata", outcome: "", note: "" };

export const SequenceTab = ({ p, onStart, onCompleteStep, onAddLog }) => (
  <>
    {(!p.sequence_steps || p.sequence_steps.length === 0) ? (
      <div className="rounded-xl border border-dashed border-border p-6 text-center">
        <p className="mb-4 text-sm text-muted-foreground">Nessuna sequenza attiva. Avvia la sequenza multicanale <b>D1 · D3 · D5 · D8 · D15</b> con promemoria automatici.</p>
        <Button onClick={onStart} data-testid="start-sequence-btn"><Play className="mr-2 h-4 w-4" /> Avvia sequenza contatti</Button>
      </div>
    ) : (
      <StepList p={p} onStart={onStart} onCompleteStep={onCompleteStep} />
    )}
    <ContactLog log={p.contact_log} onAddLog={onAddLog} />
  </>
);

function StepList({ p, onStart, onCompleteStep }) {
  const [completingIdx, setCompletingIdx] = useState(null);
  const [form, setForm] = useState({ outcome: "", note: "" });
  const todayIso = new Date().toISOString().slice(0, 10);

  const confirm = async (idx) => {
    const res = await onCompleteStep(idx, form);
    if (res) { setCompletingIdx(null); setForm({ outcome: "", note: "" }); }
  };

  return (
    <div className="space-y-2">
      <div className="mb-1 flex items-center justify-between">
        <div className="label-caps text-muted-foreground">Sequenza multicanale</div>
        {!p.sequence_active && <Button size="sm" variant="outline" onClick={onStart} data-testid="restart-sequence-btn"><Play className="mr-1.5 h-3 w-3" /> Riavvia</Button>}
      </div>
      {p.sequence_steps.map((s) => {
        const overdue = !s.done && s.due_date && s.due_date <= todayIso;
        return (
          <div key={s.index} className={`rounded-xl border p-3 ${s.done ? "border-border opacity-70" : overdue ? "border-red-500/40 bg-red-500/5" : "border-border"}`} data-testid={`seq-step-${s.index}`}>
            <div className="flex items-center gap-3">
              {s.done ? <CheckCircle2 className="h-4 w-4 shrink-0 text-[#22C55E]" /> : <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[0.65rem] text-primary">D{s.day}</span>
                  {s.channel} · {s.action}
                </div>
                <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  {s.done ? "Completato" : `Scadenza ${s.due_date}`}
                  {overdue && <span className="ml-1 text-red-400">· in ritardo</span>}
                  {s.outcome && <span className="ml-1">· {s.outcome}</span>}
                </div>
              </div>
              {!s.done && completingIdx !== s.index && (
                <Button size="sm" variant="outline" onClick={() => { setCompletingIdx(s.index); setForm({ outcome: "", note: "" }); }} data-testid={`complete-step-btn-${s.index}`}>Completa</Button>
              )}
            </div>
            {completingIdx === s.index && (
              <div className="mt-3 space-y-2 border-t border-border pt-3">
                <Select value={form.outcome} onValueChange={(v) => setForm((f) => ({ ...f, outcome: v }))}>
                  <SelectTrigger data-testid={`step-outcome-${s.index}`}><SelectValue placeholder="Esito" /></SelectTrigger>
                  <SelectContent>{OUTCOMES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
                <Textarea placeholder="Note..." rows={2} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => confirm(s.index)} data-testid={`confirm-step-${s.index}`}>Registra</Button>
                  <Button size="sm" variant="ghost" onClick={() => setCompletingIdx(null)}>Annulla</Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ContactLog({ log = [], onAddLog }) {
  const [form, setForm] = useState(EMPTY_LOG);

  const submit = async () => {
    if (!form.outcome && !form.note) { toast.error("Aggiungi un esito o una nota"); return; }
    const res = await onAddLog(form);
    if (res) setForm(EMPTY_LOG);
  };

  return (
    <div className="rounded-xl border border-border p-4">
      <div className="mb-3 flex items-center gap-2 label-caps text-muted-foreground"><MessageSquarePlus className="h-3.5 w-3.5" /> Registro tentativi</div>
      <div className="mb-3 space-y-2">
        <div className="flex gap-2">
          <Select value={form.channel} onValueChange={(v) => setForm((f) => ({ ...f, channel: v }))}>
            <SelectTrigger className="w-40" data-testid="log-channel"><SelectValue /></SelectTrigger>
            <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Esito" value={form.outcome} onChange={(e) => setForm((f) => ({ ...f, outcome: e.target.value }))} data-testid="log-outcome" />
        </div>
        <div className="flex gap-2">
          <Input placeholder="Nota" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="log-note" />
          <Button onClick={submit} data-testid="add-log-btn">Registra</Button>
        </div>
      </div>
      <div className="space-y-2">
        {log.length === 0 && <p className="text-xs text-muted-foreground">Nessun tentativo registrato.</p>}
        {log.map((c) => (
          <div key={c.id} className="rounded-lg border border-border px-3 py-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="font-medium text-foreground">{c.channel}{c.outcome ? ` · ${c.outcome}` : ""}</span>
              <span className="text-muted-foreground">{(c.at || "").slice(0, 10)}</span>
            </div>
            {c.note && <p className="mt-1 text-muted-foreground">{c.note}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
